import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Caisse() {
  const [codePin, setCodePin] = useState('');
  const [montant, setMontant] = useState('');
  const [carte, setCarte] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loadingCarte, setLoadingCarte] = useState(false);
  const [processing, setProcessing] = useState(false);

  const inputRef = useRef(null);

  // Utilisation de la configuration centralisée
  const API_BASE = config.apiBase;

  const rechercherCarte = async (e) => {
    e.preventDefault();

    if (!codePin.trim()) {
      toast.error('Code PIN requis');
      inputRef.current?.focus();
      return;
    }

    if (!/^[0-9]{4,6}$/.test(codePin)) {
      toast.error('Code PIN invalide');
      return;
    }

    setLoadingCarte(true);
    setCarte(null);
    setOperations([]);
    setMontant('');

    try {
      const response = await fetch(`${API_BASE}/pat/rechercher_carte_pin.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code_pin: codePin,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCarte(data.carte);
        toast.success('Carte trouvée');
      } else {
        toast.error(data.message || 'Carte introuvable');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoadingCarte(false);
    }
  };

  const recharger = async (e) => {
    e.preventDefault();

    if (!codePin.trim()) {
      toast.error('Code PIN requis');
      return;
    }

    const montantValue = parseInt(montant, 10);

    if (!montantValue || montantValue < 100) {
      toast.error('Montant minimum : 100 FCFA');
      return;
    }

    setProcessing(true);

    try {
      const response = await fetch(`${API_BASE}/pat/recharger_crou.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code_pin: codePin,
          montant: montantValue,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCarte((prev) => ({
          ...prev,
          ...data.carte,
          compte_crou: data.nouveau_solde_crou,
        }));

        setOperations(data.operations || []);
        setMontant('');

        toast.success(`Recharge de ${Number(data.montant_ajoute).toLocaleString()} FCFA ajoutée`);
        toast.success(`Nouveau solde CROU : ${Number(data.nouveau_solde_crou).toLocaleString()} FCFA`);
      } else {
        toast.error(data.message || 'Recharge refusée');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la recharge');
    } finally {
      setProcessing(false);
    }
  };

  const reset = () => {
    setCodePin('');
    setMontant('');
    setCarte(null);
    setOperations([]);
    inputRef.current?.focus();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-green-50 p-4 md:p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-blue-800">
            Caisse PAT - Recharge CROU
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Recherchez une carte par code PIN, vérifiez l'identité, puis ajoutez une recharge.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
          <div className="bg-white p-6 shadow">
            <h2 className="mb-1 text-xl font-black text-slate-900">
              Rechercher une carte
            </h2>
            <p className="mb-5 text-sm text-slate-500">
              Le code PIN permet de retrouver la carte.
            </p>

            <form onSubmit={rechercherCarte} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Code PIN
                </label>

                <input
                  ref={inputRef}
                  type="password"
                  value={codePin}
                  onChange={(e) => {
                    setCodePin(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setCarte(null);
                    setOperations([]);
                    setMontant('');
                  }}
                  maxLength="6"
                  placeholder="••••••"
                  className="w-full border-2 border-blue-100 bg-blue-50 px-5 py-4 text-center font-mono text-2xl font-bold tracking-[0.45em] text-slate-900 outline-none focus:border-blue-600 focus:bg-white"
                  disabled={loadingCarte || processing}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loadingCarte || processing || !codePin}
                className="w-full bg-blue-700 px-5 py-4 font-black text-white shadow hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingCarte ? 'Recherche...' : 'Afficher la carte'}
              </button>
            </form>

            {carte && (
              <button
                type="button"
                onClick={reset}
                className="mt-3 w-full bg-red-50 px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-100"
              >
                Changer de carte
              </button>
            )}
          </div>

          <div className="bg-white p-6 shadow">
            {!carte ? (
              <div className="flex min-h-72 items-center justify-center text-center">
                <div>
                  <div className="mb-3 text-5xl">🔎</div>
                  <p className="font-bold text-slate-500">
                    Aucune carte sélectionnée
                  </p>
                  <p className="mt-1 text-sm text-slate-400">
                    Les informations de l'étudiant apparaîtront ici.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid gap-5 md:grid-cols-[1fr_260px]">
                  <div>
                    <div className="mb-5 border-l-4 border-green-500 bg-green-50 p-5">
                      <div className="text-sm font-bold uppercase text-green-700">
                        Carte trouvée
                      </div>

                      <div className="mt-2 text-2xl font-black text-slate-900">
                        {carte.etudiant}
                      </div>

                      <div className="mt-2 space-y-1 text-sm text-slate-600">
                        <div className="font-mono">VDDNN : {carte.vddnn || '-'}</div>
                        <div className="font-mono">GDDNN : {carte.gddnn || '-'}</div>
                        <div>Statut validation : {carte.statut_validation || '-'}</div>
                        <div>Statut : {carte.statut || '-'}</div>
                      </div>
                    </div>

                    <div className="mb-6 bg-blue-700 p-6 text-white">
                      <div className="text-sm font-bold uppercase text-blue-100">
                        Solde CROU actuel
                      </div>

                      <div className="mt-2 text-4xl font-black">
                        {Number(carte.compte_crou || 0).toLocaleString()} FCFA
                      </div>
                    </div>

                    <form onSubmit={recharger} className="space-y-4">
                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">
                          Montant à ajouter
                        </label>

                        <input
                          type="number"
                          value={montant}
                          onChange={(e) => setMontant(e.target.value)}
                          min="100"
                          step="100"
                          placeholder="Ex: 1000"
                          className="w-full border-2 border-green-100 bg-green-50 px-5 py-4 text-center text-2xl font-black text-slate-900 outline-none focus:border-green-600 focus:bg-white"
                          disabled={processing}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={processing || !montant}
                        className="w-full bg-green-600 px-5 py-4 font-black text-white shadow hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {processing ? 'Recharge en cours...' : 'Ajouter au compte CROU'}
                      </button>
                    </form>
                  </div>

                  <div className="border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-2 text-sm font-black uppercase text-slate-600">
                      Carte d'identité
                    </div>

                    {carte.carte_identite ? (
                      <a href={carte.carte_identite} target="_blank" rel="noreferrer">
                        <img
                          src={carte.carte_identite}
                          alt="Carte d'identité"
                          className="h-72 w-full bg-white object-contain"
                        />
                      </a>
                    ) : (
                      <div className="flex h-72 items-center justify-center bg-white text-center text-sm text-slate-400">
                        Aucune carte d'identité disponible
                      </div>
                    )}

                    {carte.photo_profil && (
                      <div className="mt-3">
                        <div className="mb-2 text-xs font-bold uppercase text-slate-500">
                          Photo profil
                        </div>
                        <img
                          src={config.normalizeProfilePhoto(carte.photo_profil)}
                          alt="Profil étudiant"
                          className="h-20 w-20 rounded-full border-4 border-white object-cover shadow"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {operations.length > 0 && (
          <div className="mt-6 bg-white p-6 shadow">
            <h3 className="mb-4 text-lg font-black text-slate-900">
              Dernières opérations de cette carte
            </h3>

            <div className="max-h-80 space-y-2 overflow-y-auto">
              {operations.map((op) => (
                <div
                  key={op.id_operation}
                  className="flex items-center justify-between border-b border-slate-100 py-3"
                >
                  <div>
                    <div className="font-bold text-slate-800">
                      Recharge CROU
                    </div>
                    <div className="text-xs text-slate-400">
                      {formatDate(op.date_creation)}
                    </div>
                  </div>

                  <div className="text-lg font-black text-green-600">
                    + {Number(op.recharge || 0).toLocaleString()} FCFA
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}